import { Button, Divider, Input, Popconfirm } from 'antd';
import {
	PlusOutlined,
	MinusOutlined,
	DeleteOutlined,
	LeftOutlined,
	RightOutlined,
} from '@ant-design/icons';
import React, { FC, useRef, useState, useEffect } from 'react';
import { Document, Page } from 'react-pdf';

const OWPdfViewer: FC<any> = ({ file, fileName, showDelete, onDelete }): React.ReactElement => {
	const [numPages, setNumPages] = useState(0);
	const [zoom, setZoom] = useState<number>(0.85); // Start slightly zoomed out so full page is visible
	const [currentPage, setCurrentPage] = useState<number>(1);
	const [containerWidth, setContainerWidth] = useState<number>(800);
	const containerRef = useRef<HTMLDivElement>(null);

	const onZoomIn = () => setZoom((z) => Math.min(z + 0.1, 2));
	const onZoomOut = () => setZoom((z) => Math.max(z - 0.1, 0.5));
	const goToPrevPage = () => setCurrentPage((page) => Math.max(page - 1, 1));
	const goToNextPage = () => setCurrentPage((page) => Math.min(page + 1, numPages));

	// Update width on resize
	useEffect(() => {
		const updateWidth = () => {
			if (containerRef.current) {
				setContainerWidth(containerRef.current.offsetWidth);
			}
		};
		updateWidth();
		window.addEventListener('resize', updateWidth);
		return () => window.removeEventListener('resize', updateWidth);
	}, []);

	return (
		<div className="flex h-full flex-col items-center bg-gray-100 p-4">
			{/* Toolbar */}
			<div className="mb-4 flex w-full max-w-5xl flex-shrink-0 items-center justify-between rounded bg-white px-4 py-2 ">
				<div className="flex items-center gap-2">
					<span className="font-semibold">{fileName}</span>
				</div>

				<div className="flex items-center gap-4">
					<div className="flex items-center gap-1">
						{numPages > 0 ? (
							<>
								<Button
									icon={<LeftOutlined />}
									onClick={goToPrevPage}
									disabled={currentPage <= 1}
									size="small"
									type="text"
								/>
								<span className="text-gray-600">
									Page {currentPage} / {numPages}
								</span>
								<Button
									icon={<RightOutlined />}
									onClick={goToNextPage}
									disabled={currentPage >= numPages}
									size="small"
									type="text"
								/>
							</>
						) : (
							<span className="text-gray-600">Loading...</span>
						)}
					</div>
					<Divider type="vertical" style={{ margin: 0, borderColor: 'gray', height: '20px' }} />
					<div className="flex items-center gap-1">
						<Button onClick={onZoomOut} icon={<MinusOutlined />} size="small" type="text" />

						<Input
							size="small"
							value={`${Math.round(zoom * 100)}%`}
							onChange={(e) => {
								const raw = e.target.value.replace('%', '').trim();
								const parsed = Number.parseInt(raw || '100', 10);
								if (Number.isNaN(parsed)) return;
								const clamped = Math.min(200, Math.max(50, parsed));
								setZoom(clamped / 100);
							}}
							style={{ width: 70, textAlign: 'center' }}
						/>
						<Button onClick={onZoomIn} icon={<PlusOutlined />} size="small" type="text" />
					</div>
				</div>

				{showDelete ? (
					<div className="flex items-center gap-2">
						<Popconfirm
							title="Are you sure you want to delete this PDF?"
							onConfirm={onDelete}
							okText="Yes"
							cancelText="No"
						>
							<Button icon={<DeleteOutlined />} type="text" />
						</Popconfirm>
					</div>
				) : null}
			</div>

			<div
				className="flex w-full max-w-5xl flex-1 justify-center overflow-auto border border-gray-200 bg-white p-4"
				ref={containerRef}
				style={{ alignItems: 'flex-start' }}
			>
				<Document file={file} onLoadSuccess={({ numPages }) => setNumPages(numPages)}>
					<Page pageNumber={currentPage} scale={zoom} width={Math.min(containerWidth, 800)} />
				</Document>
			</div>
		</div>
	);
};

export default OWPdfViewer;
